import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import {
    CloudOff as CloudOffIcon,
    CloudQueue as CloudQueueIcon,
    CloudDone as CloudDoneIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import timesheetService, { SYNC_STATUS } from '../../services/timesheetService';
import timesheetSyncService, { EVT_SYNC_STATE } from '../../services/timesheetSyncService';

const POLL_INTERVAL_MS = 5000;

/**
 * Баннер-индикатор состояния синхронизации.
 * Скрыт, когда всё ok (online + 0 pending/error/conflict).
 *
 * Кнопка "Retry now":
 *   - Сбрасывает все error-записи в pending (сервисный heal)
 *   - Форсирует sync прямо сейчас (в обход backoff'а)
 *   - Доступна когда есть error или pending-в-backoff
 */
export default function TimesheetSyncStatusBar() {
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [pendingCount, setPendingCount] = useState(0);
    const [errorCount, setErrorCount] = useState(0);
    const [conflictCount, setConflictCount] = useState(0);
    const [isSyncing, setIsSyncing] = useState(false);
    const [inBackoff, setInBackoff] = useState(false);
    const [retrying, setRetrying] = useState(false);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    // Подписка на события сервиса. После каждого tick он шлёт актуальные счётчики.
    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);
        const handleSyncState = (e) => {
            const d = e.detail || {};
            if (typeof d.isOnline === 'boolean') setIsOnline(d.isOnline);
            if (typeof d.isSyncing === 'boolean') setIsSyncing(d.isSyncing);
            if (typeof d.inBackoff === 'boolean') setInBackoff(d.inBackoff);
            if (typeof d.pendingCount === 'number') setPendingCount(d.pendingCount);
            if (typeof d.errorCount === 'number') setErrorCount(d.errorCount);
            if (typeof d.conflictCount === 'number') setConflictCount(d.conflictCount);
        };
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        window.addEventListener(EVT_SYNC_STATE, handleSyncState);
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener(EVT_SYNC_STATE, handleSyncState);
        };
    }, []);

    // Фолбэк-опрос IndexedDB на случай если сервис долго не тикает
    // (например сразу после mount пока сервис не сделал первый tick).
    useEffect(() => {
        let cancelled = false;
        const refresh = async () => {
            try {
                const [pending, errors, conflicts] = await Promise.all([
                    timesheetService.getRecordsBySyncStatus(SYNC_STATUS.PENDING),
                    timesheetService.getRecordsBySyncStatus(SYNC_STATUS.ERROR),
                    timesheetService.getRecordsBySyncStatus(SYNC_STATUS.CONFLICT),
                ]);
                if (cancelled || !mountedRef.current) return;
                setPendingCount(pending.length);
                setErrorCount(errors.length);
                setConflictCount(conflicts.length);
            } catch (e) {
                console.error('[SyncStatusBar] refresh failed:', e);
            }
        };
        refresh();
        const interval = setInterval(refresh, POLL_INTERVAL_MS);
        return () => { cancelled = true; clearInterval(interval); };
    }, []);

    const handleRetryClick = useCallback(async () => {
        if (retrying || !isOnline) return;
        setRetrying(true);
        try {
            await timesheetSyncService.forceSyncNow({ healFirst: true });
        } catch (e) {
            console.warn('[SyncStatusBar] retry failed:', e);
        } finally {
            if (mountedRef.current) setRetrying(false);
        }
    }, [retrying, isOnline]);

    // Ничего не показывать когда всё чисто
    if (isOnline && pendingCount === 0 && errorCount === 0 && conflictCount === 0) return null;

    let bgcolor, borderColor, icon, text;
    const showRetry = isOnline && (errorCount > 0 || (pendingCount > 0 && inBackoff && !isSyncing));

    if (!isOnline) {
        bgcolor = '#f8d7da';
        borderColor = '#dc3545';
        icon = <CloudOffIcon fontSize="small" sx={{ color: '#721c24' }} />;
        text = pendingCount > 0
            ? `Offline. ${pendingCount} ${plural(pendingCount, 'record')} pending sync.`
            : 'Offline. Changes will sync when connection is restored.';
    } else if (errorCount > 0) {
        bgcolor = '#fee2e2';
        borderColor = '#dc2626';
        icon = <ErrorIcon fontSize="small" sx={{ color: '#991b1b' }} />;
        text = `Sync error: ${errorCount} ${plural(errorCount, 'record')}. Auto-retry every 5 min.`;
    } else if (conflictCount > 0) {
        bgcolor = '#fef3c7';
        borderColor = '#d97706';
        icon = <WarningIcon fontSize="small" sx={{ color: '#92400e' }} />;
        text = `${conflictCount} ${plural(conflictCount, 'record')} need attention (daily limit / version conflict).`;
    } else if (isSyncing) {
        bgcolor = '#dbeafe';
        borderColor = '#2563eb';
        icon = <CircularProgress size={14} thickness={5} />;
        text = `Syncing… ${pendingCount} ${plural(pendingCount, 'record')}.`;
    } else if (pendingCount > 0) {
        bgcolor = '#fef3c7';
        borderColor = '#d97706';
        icon = <CloudQueueIcon fontSize="small" sx={{ color: '#92400e' }} />;
        text = inBackoff
            ? `${pendingCount} ${plural(pendingCount, 'record')} queued (retry paused).`
            : `${pendingCount} ${plural(pendingCount, 'record')} queued for sync.`;
    } else {
        bgcolor = '#dcfce7';
        borderColor = '#16a34a';
        icon = <CloudDoneIcon fontSize="small" sx={{ color: '#14532d' }} />;
        text = 'All synced.';
    }

    return (
        <Box
            role="status"
            aria-live="polite"
            sx={{
                // Плавающая пилюля НАД контентом: появление/исчезновение не меняет
                // высоту страницы (раньше баннер в потоке дёргал весь грид).
                position: 'fixed',
                top: 8,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1400,
                maxWidth: 'min(92vw, 560px)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.5,
                bgcolor,
                border: '1.5px solid',
                borderColor,
                borderRadius: 999,
                boxShadow: '0 4px 14px rgba(15, 23, 42, 0.18)',
            }}
        >
            {icon}
            <Typography variant="body2" sx={{ color: 'text.primary', fontSize: '0.8rem', flexGrow: 1 }}>
                {text}
            </Typography>
            {showRetry && (
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={retrying
                        ? <CircularProgress size={12} thickness={5} />
                        : <RefreshIcon fontSize="small" />}
                    onClick={handleRetryClick}
                    disabled={retrying}
                    sx={{
                        py: 0.25,
                        minHeight: 0,
                        textTransform: 'none',
                        fontSize: '0.75rem',
                    }}
                >
                    {retrying ? 'Retrying…' : 'Retry now'}
                </Button>
            )}
        </Box>
    );
}

function plural(n, word) {
    return n === 1 ? word : `${word}s`;
}
